// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;


function isNewSupabaseApiKey(value: string): boolean {
  return value.startsWith('sb_publishable_') || value.startsWith('sb_secret_');
}

function createSupabaseFetch(supabaseKey: string): typeof fetch {
  return (input, init) => {
    const headers = new Headers(
      typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined,
    );

    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }

    // New Supabase API keys are opaque strings, not bearer JWTs.
    if (isNewSupabaseApiKey(supabaseKey) && headers.get('Authorization') === `Bearer ${supabaseKey}`) {
      headers.delete('Authorization');
    }

    headers.set('apikey', supabaseKey);
    return fetch(input, { ...init, headers });
  };
}

// On a Lovable preview surface, store the session in a cookie on the shared
// <projectId> parent so preview surfaces share one login (else localStorage).
function sharedPreviewStorage() {
  if (typeof window === 'undefined') return undefined;
  const parent = location.hostname.match(/^[^.]+\.([0-9a-f-]{36}\.lovableproject(?:-dev)?\.com)$/)?.[1];
  if (!parent) return localStorage;
  const attrs = `; Domain=${parent}; Path=/; SameSite=None; Secure; Partitioned`;
  const persist = `${attrs}; Max-Age=31536000`;
  const CHUNK = 3600, MAX_CHUNKS = 64;
  const raw = (n: string) =>
    document.cookie.match(new RegExp('(?:^|; )' + n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'))?.[1];
  const readCookie = (k: string) => {
    let enc: string | undefined;
    if (raw(`${k}.0`) !== undefined) {
      enc = '';
      for (let i = 0; i < MAX_CHUNKS; i++) {
        const p = raw(`${k}.${i}`);
        if (p === undefined) break;
        enc += p;
      }
    } else {
      enc = raw(k);
    }
    if (enc === undefined) return null;
    try { return decodeURIComponent(enc); } catch { return null; }
  };
  const clearCookie = (k: string) => {
    document.cookie = `${k}=${attrs}; Max-Age=0`;
    for (let i = 0; i < MAX_CHUNKS && raw(`${k}.${i}`) !== undefined; i++) document.cookie = `${k}.${i}=${attrs}; Max-Age=0`;
  };
  return {
    getItem: (k: string) => {
      const c = readCookie(k);
      return c !== null ? c : localStorage.getItem(k);
    },
    setItem: (k: string, value: string) => {
      clearCookie(k);
      const enc = encodeURIComponent(value);
      if (enc.length <= CHUNK) document.cookie = `${k}=${enc}${persist}`;
      else for (let i = 0, o = 0; o < enc.length; i++, o += CHUNK) document.cookie = `${k}.${i}=${enc.slice(o, o + CHUNK)}${persist}`;
      // Safari/WebKit ITP can block the partitioned cross-site cookie; fall back
      // to localStorage so the session survives per-origin instead of vanishing.
      if (readCookie(k) === value) localStorage.removeItem(k);
      else localStorage.setItem(k, value);
    },
    removeItem: (k: string) => { clearCookie(k); localStorage.removeItem(k); },
  };
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  global: {
    fetch: createSupabaseFetch(SUPABASE_PUBLISHABLE_KEY),
  },
  auth: {
    storage: sharedPreviewStorage(),
    persistSession: true,
    autoRefreshToken: true,
  }
});
